import { Menu, Select } from "antd";
import React, { ReactNode } from "react";
import { Headset, useSelectedHeadset } from "../hooks/selectedHeadset";

export type ManualData = {
  title: string;
  sections?: {
    headsetTypes?: Headset[];
    subtitle?: string;
    content: ReactNode;
  }[];
  content?: ReactNode;
  headsetTypes?: Headset[];
};

type Props = {
  data: ManualData[];
  title?: string;
  subtitle?: string;
  showSideBar?: boolean;
  showHeadsetSelection?: boolean;
};

const matchesHeadset = (
  headsetTypes: Headset[] | undefined,
  active: Headset | undefined
) => !headsetTypes || headsetTypes.includes(active || "Quest");

const Manual = (props: Props) => {
  const { selectedHeadset, setSelectedHeadset } = useSelectedHeadset();

  const visibleSections = props.data.filter((s) =>
    matchesHeadset(s.headsetTypes, selectedHeadset)
  );

  const renderSideBar = () => {
    if (props.showSideBar === false) return null;
    return (
      <aside className="manual-sidebar">
        <div className="manual-sidebar-title">On this page</div>
        <Menu
          openKeys={visibleSections.map(({ title }) => title)}
          forceSubMenuRender
          mode="inline"
        >
          {visibleSections.map(({ title, sections }, index) => {
            const titleNode = (
              <a className="manual-sidebar-link" href={`#${title}`}>
                <span className="manual-sidebar-number">{index + 1}</span>
                <span className="manual-sidebar-label">{title}</span>
              </a>
            );

            if (!sections)
              return <Menu.Item key={title}>{titleNode}</Menu.Item>;

            const visibleSubs = sections.filter((s) =>
              matchesHeadset(s.headsetTypes, selectedHeadset)
            );

            return (
              <Menu.SubMenu
                style={{ cursor: "auto" }}
                key={title}
                title={titleNode}
              >
                {visibleSubs.map(({ subtitle }) => (
                  <Menu.Item key={subtitle}>
                    <a
                      className="manual-sidebar-sublink"
                      href={`#${subtitle}`}
                    >
                      {subtitle}
                    </a>
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            );
          })}
        </Menu>
      </aside>
    );
  };

  const renderContent = () => {
    return (
      <div className="manual-content" id="content">
        {visibleSections.map(({ sections, content, title }, index) => {
          const visibleSubs =
            sections?.filter((s) =>
              matchesHeadset(s.headsetTypes, selectedHeadset)
            ) ?? [];

          return (
            <section
              key={title + "_section"}
              id={title}
              className="manual-section"
            >
              <h2 className="manual-section-title">
                <span className="manual-section-number">{index + 1}</span>
                <span>{title}</span>
              </h2>

              {content ? (
                <div className="manual-section-body">{content}</div>
              ) : (
                visibleSubs.map(({ content, subtitle }) => (
                  <section
                    key={subtitle + "_section"}
                    id={subtitle}
                    className="manual-subsection"
                  >
                    <h3 className="manual-subsection-title">{subtitle}</h3>
                    <div className="manual-subsection-body">{content}</div>
                  </section>
                ))
              )}
            </section>
          );
        })}
      </div>
    );
  };

  const showHeader =
    Boolean(props.title) || props.showHeadsetSelection !== false;

  return (
    <div className="manual-page" style={{ width: "100%" }}>
      {showHeader && (
        <div className="manual-page-header">
          {props.title && (
            <div className="manual-page-header-text">
              <h1>{props.title}</h1>
              {props.subtitle && <p>{props.subtitle}</p>}
            </div>
          )}
          {props.showHeadsetSelection !== false && (
            <div className="manual-headset-picker">
              <span className="manual-headset-picker-label">Headset</span>
              <HeadsetSelect
                style={{ width: 200 }}
                onChange={setSelectedHeadset}
                value={selectedHeadset}
              />
            </div>
          )}
        </div>
      )}

      <div className="manual-grid">
        {renderSideBar()}
        {renderContent()}
      </div>
    </div>
  );
};
export default Manual;

type HeadsetSelectProps = {
  onChange: (value: Headset) => void;
  value: Headset | undefined;
  style?: React.CSSProperties;
};
export const HeadsetSelect = (props: HeadsetSelectProps) => {
  return (
    <Select
      size="large"
      style={{ ...props.style }}
      onChange={props.onChange}
      value={props.value || "Pico"}
    >
      <Select.Option key={"Pico"}>
        <HeadsetLogo headset="Pico" /> Pico 4
      </Select.Option>
      <Select.Option key={"Quest"}>
        <HeadsetLogo headset="Quest" /> Meta Quest
      </Select.Option>
      <Select.Option key={"Focus"}>
        <HeadsetLogo headset="Focus" /> Vive Focus
      </Select.Option>
    </Select>
  );
};

const HeadsetLogo = (props: { headset: Headset }) => {
  let logo;
  switch (props.headset) {
    case "Quest":
      logo = "/images/logos/MetaLogo.png";
      break;
    case "Focus":
      logo = "/images/logos/ViveLogo.png";
      break;
    case "Pico":
      logo = "/images/logos/PicoLogo.png";
      break;
  }
  if (!logo) return null;

  return <img alt="" style={{ width: 24 }} src={logo} />;
};
